import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FlaskConical, Leaf, Loader2, Plus } from 'lucide-react';
import { Card, CardContent } from '../ui/Card';
import Button from '../ui/Button';
import soilService from '../../services/soil';
import { errorMessage } from '../../utils/apiResponse';
import { formatLocalDateInTz, useOrgTimezone } from '../../utils/timezone';
import { depthText, fmtNum, showValue, trendAnalytes, trendSamples } from '../../utils/soilAnalytes';
import { cn } from '../../utils/cn';
import NutrientTrendChart from './NutrientTrendChart';
import type { SampleType, SoilSample } from '../../types/soil';

/** The figures a grower looks at first, in this order, where the sample has them. */
const HEADLINE = ['ph_cacl2', 'ph_water', 'phosphorus_colwell', 'potassium_colwell', 'organic_carbon', 'ec'];
const LEAF_HEADLINE = ['leaf_n', 'leaf_p', 'leaf_k', 'leaf_ca', 'leaf_mg', 'leaf_b'];
const SHORT: Record<string, string> = {
  ph_cacl2: 'pH (CaCl₂)', ph_water: 'pH (water)', phosphorus_colwell: 'P', potassium_colwell: 'K', organic_carbon: 'OC', ec: 'EC',
  leaf_n: 'N', leaf_p: 'P', leaf_k: 'K', leaf_ca: 'Ca', leaf_mg: 'Mg', leaf_b: 'B',
};

function headline(s: SoilSample): string {
  if (s.sample_type === 'leaf') {
    const leaf = LEAF_HEADLINE
      .map((k) => s.results.find((r) => r.analyte === k))
      .filter((r): r is NonNullable<typeof r> => !!r)
      .slice(0, 4)
      .map((r) => `${SHORT[r.analyte]} ${showValue(r)}${r.unit === '%' ? '%' : ''}`);
    return leaf.join(' · ') || `${s.results.length} results`;
  }
  const parts = HEADLINE
    .map((k) => s.results.find((r) => r.analyte === k))
    .filter((r): r is NonNullable<typeof r> => !!r)
    .slice(0, 4)
    .map((r) => `${SHORT[r.analyte]} ${showValue(r)}${r.analyte === 'organic_carbon' ? '%' : ''}`);
  if (s.derived.ecec != null) parts.push(`ECEC ${fmtNum(s.derived.ecec)}`);
  return parts.join(' · ') || `${s.results.length} results`;
}

/**
 * Soil samples taken in a block or a cultivar area. On a cultivar area it
 * shows the area's own samples and its block's samples not taken in another
 * area, since a block sample describes every planting in it.
 */
export default function SoilSamplesPanel({ blockId, areaId }: { blockId: number; areaId?: number }) {
  const navigate = useNavigate();
  const timezone = useOrgTimezone();
  const [samples, setSamples] = useState<SoilSample[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await soilService.list(areaId ? { cultivar_area_id: areaId } : { block_id: blockId });
      setSamples(res.success && res.data ? res.data : []);
      setError(null);
    } catch (e) {
      setError(errorMessage(e, 'Could not load the soil samples'));
    }
  }, [areaId, blockId]);
  useEffect(() => { load(); }, [load]);

  // The trend is this exact place: the planting's own samples, or on a block
  // page the whole-block ones. A sample from another planting is another place.
  const trends = useMemo(() => {
    const out: Partial<Record<SampleType, SoilSample[]>> = {};
    for (const t of ['soil', 'leaf'] as const) {
      const here = trendSamples(samples ?? [], t, areaId ?? null);
      if (trendAnalytes(here, t).length) out[t] = here;
    }
    return out;
  }, [samples, areaId]);
  const trendTypes = (Object.keys(trends) as SampleType[]);
  const [trendType, setTrendType] = useState<SampleType | null>(null);
  const shownTrend = trendType && trends[trendType] ? trendType : trendTypes[0] ?? null;

  const add = (type: 'soil' | 'leaf') =>
    navigate(`/orchard/soil/new?block_id=${blockId}${areaId ? `&cultivar_area_id=${areaId}` : ''}${type === 'leaf' ? '&type=leaf' : ''}`);

  return (
    <Card>
      <CardContent className="p-5 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-2 min-w-0">
            <FlaskConical className="h-4 w-4 text-neutral-500 mt-0.5 flex-shrink-0" />
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-neutral-700">Soil and leaf analysis</h3>
              <p className="text-xs text-neutral-500">Lab results, typed in or read from the lab's report.</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => add('soil')}>
              <Plus className="h-4 w-4" />
              Soil sample
            </Button>
            <Button size="sm" variant="outline" onClick={() => add('leaf')}>
              <Leaf className="h-4 w-4" />
              Leaf sample
            </Button>
          </div>
        </div>

        {error && <p className="text-sm text-error-700">{error}</p>}
        {!samples && !error && (
          <div className="flex justify-center py-3"><Loader2 className="h-5 w-5 text-neutral-400 animate-spin" /></div>
        )}
        {samples && samples.length === 0 && (
          <p className="text-sm text-neutral-500">No soil or leaf samples yet. Upload the lab's report and its figures are read for you to check.</p>
        )}
        {samples && samples.length > 0 && (
          <ul className="divide-y divide-neutral-100">
            {samples.slice(0, 6).map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  className="w-full text-left py-2 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 text-sm hover:bg-neutral-50 rounded"
                  onClick={() => navigate(`/orchard/soil/${s.id}`)}
                >
                  <span className="text-neutral-800">
                    {s.sample_type === 'leaf' && <Leaf className="inline h-3.5 w-3.5 text-green-700 mr-1 -mt-0.5" aria-label="Leaf" />}
                    {formatLocalDateInTz(s.sampled_on, timezone)}
                    {depthText(s.depth_from_cm, s.depth_to_cm) && <span className="text-neutral-500"> · {depthText(s.depth_from_cm, s.depth_to_cm)}</span>}
                    {areaId && !s.cultivar_area_id && <span className="text-neutral-500"> · whole block</span>}
                    {!areaId && s.cultivar_area_name && <span className="text-neutral-500"> · {s.cultivar_area_name}</span>}
                  </span>
                  <span className="text-neutral-600 break-words">{headline(s)}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
        {shownTrend && trends[shownTrend] && (
          <div className="border-t border-neutral-100 pt-3 space-y-2">
            {trendTypes.length > 1 && (
              <div className="flex gap-1" role="radiogroup" aria-label="Trend of">
                {trendTypes.map((t) => (
                  <button
                    key={t}
                    type="button"
                    role="radio"
                    aria-checked={shownTrend === t}
                    onClick={() => setTrendType(t)}
                    className={cn('px-2.5 py-1 rounded-full border text-xs',
                      shownTrend === t ? 'bg-primary-50 border-primary-300 text-primary-800' : 'border-neutral-200 text-neutral-600')}
                  >
                    {t === 'leaf' ? 'Leaf' : 'Soil'}
                  </button>
                ))}
              </div>
            )}
            <NutrientTrendChart
              key={shownTrend}
              samples={trends[shownTrend] as SoilSample[]}
              type={shownTrend}
              caption={shownTrend === 'soil'
                ? [areaId ? null : 'Whole block', depthText(trends.soil?.[0]?.depth_from_cm ?? null, trends.soil?.[0]?.depth_to_cm ?? null)].filter(Boolean).join(', ')
                : areaId ? undefined : 'Whole block'}
            />
          </div>
        )}

        {samples && samples.length > 6 && (
          <button type="button" className="text-sm text-primary-600 hover:underline" onClick={() => navigate(`/orchard/soil?block_id=${blockId}`)}>
            All {samples.length} samples
          </button>
        )}
      </CardContent>
    </Card>
  );
}
